import React, { useRef, useState, useEffect } from "react";
import { formatDistanceToNow } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCar,
  faCamera,
  faComment,
  faTag,
  faTrophy,
  faBolt,
  faCalendarCheck,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import { API_ENDPOINTS } from "../config/constants";
import ImageCropper from "./ImageCropper";

const SpotterLicense = ({ user, isEditable = false }) => {
  if (!user) return null;

  const [imageFile, setImageFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [profilePic, setProfilePic] = useState(user.profilePic);
  const [profilePicVariants, setProfilePicVariants] = useState(
    user.profilePicVariants
  );
  const [showCropper, setShowCropper] = useState(false);
  const fileInputRef = useRef(null);
  const cardRef = useRef(null);
  const [rotation, setRotation] = useState({ x: 0, y: 0 });

  const stats = user.spotterStats || {};
  const joinDate = new Date(user.createdAt);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!cardRef.current) return;

      const rect = cardRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Convert position to rotation degrees (-15 to 15 degrees)
      const rotateY = (x / rect.width - 0.5) * 30;
      const rotateX = (y / rect.height - 0.5) * -30;

      setRotation({ x: rotateX, y: rotateY });
    };

    const handleMouseLeave = () => {
      setRotation({ x: 0, y: 0 });
    };

    const card = cardRef.current;
    if (card) {
      card.addEventListener("mousemove", handleMouseMove);
      card.addEventListener("mouseleave", handleMouseLeave);
    }

    return () => {
      if (card) {
        card.removeEventListener("mousemove", handleMouseMove);
        card.removeEventListener("mouseleave", handleMouseLeave);
      }
    };
  }, []);

  const handleImageUpload = async (croppedFile) => {
    try {
      setIsUploading(true);
      setError(null);

      const formData = new FormData();
      formData.append("image", croppedFile);
      formData.append("userId", user._id);

      const response = await fetch(API_ENDPOINTS.UPLOAD_PROFILE_PIC, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to upload image");
      }

      const data = await response.json();
      setProfilePic(data.profilePic);
      setProfilePicVariants(data.variants);
      setError(null);
    } catch (err) {
      setError("Failed to upload image. Please try again.");
    } finally {
      setIsUploading(false);
      setShowCropper(false);
      setImageFile(null);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setShowCropper(true);
    }
  };

  const handleImageClick = () => {
    if (isEditable) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className="relative max-w-md mx-auto">
      {/* Main license card */}
      <div
        ref={cardRef}
        className="relative bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden transition-transform duration-200 ease-out transform-gpu dark:bg-gray-800 dark:border-gray-700 dark:shadow-lg"
        style={{
          transform: `perspective(1000px) rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`,
        }}
      >
        {/* Holographic effect overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-400/30 via-purple-400/30 to-pink-400/30 opacity-30 mix-blend-overlay pointer-events-none rounded-xl" />

        {/* Shine effect overlay - moved outside header for full card coverage */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div
            className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/20 to-transparent shine-animation"
            style={{
              width: "500%",
              height: "500%",
              top: "-200%",
              left: "-200%",
              transform: "rotate(-45deg)",
            }}
          />
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white px-6 py-4">
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center justify-between gap-4">
                <h2 className="text-sm font-medium opacity-90 text-blue-100">
                  SPOTTER'S LICENSE
                </h2>
                <span className="text-[10px] text-blue-200/60">
                  ISSUED {joinDate.toLocaleDateString()}
                </span>
              </div>
              <h1 className="text-2xl font-bold text-white mt-1 truncate">
                @{user.username}
              </h1>
              <div className="mt-1 font-mono text-xs text-blue-100 opacity-80">
                {user._id}
              </div>
            </div>
            {/* Profile Picture */}
            <div
              className="relative w-20 h-20 bg-gray-200 rounded-lg border-4 border-white shadow-md overflow-hidden cursor-pointer group"
              onClick={handleImageClick}
            >
              {profilePicVariants?.large || profilePic ? (
                <img
                  src={profilePicVariants?.large || profilePic}
                  alt={user.username}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-blue-50">
                  <span className="text-2xl text-blue-300">
                    {user.username.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              {isEditable && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <FontAwesomeIcon icon={faUpload} className="text-white" />
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileSelect}
            />
          </div>
        </div>

        {/* License content */}
        <div className="p-4 space-y-4 dark:bg-gray-800">
          {/* Stats squares */}
          <div className="grid grid-cols-4 gap-3">
            <div className="aspect-square bg-blue-50 rounded-lg flex flex-col items-center justify-center p-2 dark:bg-blue-900 dark:text-blue-100">
              <FontAwesomeIcon icon={faCar} className="text-blue-500 mb-1" />
              <div className="text-lg font-bold text-blue-700">
                {stats.totalSpots || 0}
              </div>
              <div className="text-[10px] text-blue-600 font-medium">SPOTS</div>
            </div>
            <div className="aspect-square bg-green-50 rounded-lg flex flex-col items-center justify-center p-2 dark:bg-green-900 dark:text-green-100">
              <FontAwesomeIcon
                icon={faCamera}
                className="text-green-500 mb-1"
              />
              <div className="text-lg font-bold text-green-700">
                {stats.totalImages || 0}
              </div>
              <div className="text-[10px] text-green-600 font-medium">
                IMAGES
              </div>
            </div>
            <div className="aspect-square bg-purple-50 rounded-lg flex flex-col items-center justify-center p-2 dark:bg-purple-900 dark:text-purple-100">
              <FontAwesomeIcon
                icon={faComment}
                className="text-purple-500 mb-1"
              />
              <div className="text-lg font-bold text-purple-700">
                {stats.totalComments || 0}
              </div>
              <div className="text-[10px] text-purple-600 font-medium">
                COMMENTS
              </div>
            </div>
            <div className="aspect-square bg-orange-50 rounded-lg flex flex-col items-center justify-center p-2 dark:bg-orange-900 dark:text-orange-100">
              <FontAwesomeIcon icon={faTag} className="text-orange-500 mb-1" />
              <div className="text-lg font-bold text-orange-700">
                {stats.totalTags || 0}
              </div>
              <div className="text-[10px] text-orange-600 font-medium">
                TAGS
              </div>
            </div>
          </div>

          {/* Streaks and achievements */}
          <div className="flex flex-wrap gap-2">
            {stats.mostSpottedMake?.make && (
              <div className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 px-2 py-1 rounded-full text-xs dark:bg-blue-900 dark:text-blue-100">
                <FontAwesomeIcon
                  icon={faCar}
                  className="text-blue-500 w-3 h-3"
                />
                <span className="opacity-75">Most spotted:</span>
                <strong>{stats.mostSpottedMake.make}</strong>
                <span className="opacity-75">
                  ({stats.mostSpottedMake.count})
                </span>
              </div>
            )}
            {stats.longestStreak?.days > 0 && (
              <div className="inline-flex items-center gap-1.5 bg-yellow-50 text-yellow-700 px-2 py-1 rounded-full text-xs dark:bg-yellow-900 dark:text-yellow-100">
                <FontAwesomeIcon
                  icon={faTrophy}
                  className="text-yellow-500 w-3 h-3"
                />
                <span className="opacity-75">Best streak:</span>
                <strong>{stats.longestStreak.days} days</strong>
              </div>
            )}
            {stats.currentStreak?.days > 0 && (
              <div className="inline-flex items-center gap-1.5 bg-orange-50 text-orange-700 px-2 py-1 rounded-full text-xs dark:bg-orange-900 dark:text-orange-100">
                <FontAwesomeIcon
                  icon={faBolt}
                  className="text-orange-500 w-3 h-3"
                />
                <span className="opacity-75">Current:</span>
                <strong>{stats.currentStreak.days} days</strong>
              </div>
            )}
            {stats.lastSpotDate && (
              <div className="inline-flex items-center gap-1.5 bg-green-50 text-green-700 px-2 py-1 rounded-full text-xs dark:bg-green-900 dark:text-green-100">
                <FontAwesomeIcon
                  icon={faCalendarCheck}
                  className="text-green-500 w-3 h-3"
                />
                <span className="opacity-75">Last spot:</span>
                <strong>
                  {formatDistanceToNow(new Date(stats.lastSpotDate))} ago
                </strong>
              </div>
            )}
          </div>
        </div>

        {/* Footer with watermark */}
        <div className="px-4 py-2.5 bg-gray-50 text-xs text-gray-500 flex justify-between items-center dark:bg-gray-800 dark:text-gray-400">
          <span>
            Stats last updated:{" "}
            {stats.updatedAt
              ? formatDistanceToNow(new Date(stats.updatedAt)) + " ago"
              : "Never"}
          </span>
          <div className="bg-gradient-to-r from-gray-200 to-gray-300 px-2 py-0.5 rounded text-gray-600 text-[8px] font-medium tracking-wide dark:bg-gray-700 dark:text-gray-300 dark:from-gray-700 dark:to-gray-800">
            G80 OR NOT
          </div>
        </div>
      </div>

      {error && (
        <div className="absolute bottom-0 left-0 right-0 bg-red-500 text-white text-sm py-2 px-4">
          {error}
        </div>
      )}

      {showCropper && imageFile && (
        <ImageCropper
          imageFile={imageFile}
          onCropComplete={handleImageUpload}
          onCancel={() => {
            setShowCropper(false);
            setImageFile(null);
          }}
          aspect={1} // Square aspect ratio for avatar
        />
      )}

      <style>
        {`
          .shine-animation {
            animation: shine 12s infinite cubic-bezier(0.4, 0, 0.2, 1);
          }

          @keyframes shine {
            0% {
              transform: translate(-100%, -100%) rotate(-45deg);
            }
            100% {
              transform: translate(100%, 100%) rotate(-45deg);
            }
          }
        `}
      </style>
    </div>
  );
};

export default SpotterLicense;
